/**
 * TEAM_LEAD capability provere za OCD/instituciju kontekst.
 * Kombinuje JWT uloge (`obligationAuth`) sa aktivnim legal subject-om iz preferences.
 */
import UserPreferencesService from '@/shared/services/UserPreferencesService';
import {
  isJwtTeamLead,
  isPlatformAdmin,
} from '@/shared/auth/obligationAuth';

const normalizeId = (value) => {
  if (value == null) return '';
  return String(value).trim();
};

const idsEqual = (left, right) => {
  const a = normalizeId(left);
  const b = normalizeId(right);
  return Boolean(a && b && a === b);
};

/**
 * Vraća ID aktivnog pravnog subjekta iz korisničkih preferences.
 * @param {object} [preferences] - Korisničke preferences (`userType`, `selectedInstitutionId`, …).
 * @returns {string} Normalizovan ID institucije ili organizacije.
 */
export const getSelectedLegalSubjectId = (preferences = {}) =>
  preferences.userType === 'institution'
    ? normalizeId(preferences.selectedInstitutionId)
    : normalizeId(preferences.selectedOrganizationId);

/** @deprecated Koristiti `isPlatformAdmin` iz `obligationAuth`. */
export const hasAdminRole = (user) => isPlatformAdmin(user);

/**
 * Proverava da li je korisnik TEAM_LEAD za datu organizaciju u aktivnom kontekstu.
 * @param {object|null|undefined} user - Autentifikovani korisnik.
 * @param {string|number|null|undefined} organizationId - ID ciljne organizacije.
 * @param {object|null} [preferences] - Preferences (podrazumevano iz `UserPreferencesService`).
 * @returns {boolean} `true` ako je JWT TEAM_LEAD i organizacija odgovara aktivnom kontekstu.
 */
export const isTeamLeadForOrganization = (user, organizationId, preferences = null) => {
  if (!isJwtTeamLead(user)) return false;
  const targetOrganizationId = normalizeId(organizationId);
  if (!targetOrganizationId) return false;
  const prefs = preferences ?? UserPreferencesService.getPreferences() ?? {};
  return idsEqual(targetOrganizationId, getSelectedLegalSubjectId(prefs));
};

/**
 * Da li korisnik može odobriti pristup organizaciji.
 * @param {object} params
 * @param {object|null|undefined} params.user - Autentifikovani korisnik.
 * @param {string|number|null|undefined} params.organizationId - ID organizacije.
 * @returns {boolean}
 */
export const canApproveOrganizationAccess = ({ user, organizationId }) =>
  isPlatformAdmin(user) || isTeamLeadForOrganization(user, organizationId);

/**
 * Da li korisnik može upravljati članovima organizacije.
 * @param {object} params
 * @param {object|null|undefined} params.user - Autentifikovani korisnik.
 * @param {string|number|null|undefined} params.organizationId - ID organizacije.
 * @returns {boolean}
 */
export const canManageOrganizationMembers = ({ user, organizationId }) =>
  isPlatformAdmin(user) || isTeamLeadForOrganization(user, organizationId);

/**
 * Da li korisnik može dodeliti ili opozvati TEAM_LEAD ulogu.
 * @param {object} params
 * @param {object|null|undefined} params.user - Autentifikovani korisnik.
 * @param {string|number|null|undefined} params.organizationId - ID organizacije.
 * @returns {boolean}
 */
export const canAssignOrRevokeTeamLead = ({ user, organizationId }) =>
  isPlatformAdmin(user) || isTeamLeadForOrganization(user, organizationId);

/**
 * Da li korisnik može uskladiti obavezu kao odgovorna institucija lead.
 * @param {object} params
 * @param {object|null|undefined} params.user - Autentifikovani korisnik.
 * @param {string|number|null|undefined} params.responsibleOrganizationId - ID odgovorne organizacije.
 * @param {object|null} [params.userPreferences] - Eksplicitne preferences (opciono).
 * @returns {boolean}
 */
export const canAlignObligationAsResponsibleInstitutionLead = ({
  user,
  responsibleOrganizationId,
  userPreferences = null,
}) =>
  isPlatformAdmin(user)
  || isTeamLeadForOrganization(user, responsibleOrganizationId, userPreferences);

/**
 * Da li korisnik može dodeliti moderatore za odgovornu instituciju.
 * @param {object} params
 * @param {object|null|undefined} params.user - Autentifikovani korisnik.
 * @param {string|number|null|undefined} params.responsibleOrganizationId - ID odgovorne organizacije.
 * @param {object|null} [params.userPreferences] - Eksplicitne preferences (opciono).
 * @returns {boolean}
 */
export const canAssignModeratorsForResponsibleInstitution = ({
  user,
  responsibleOrganizationId,
  userPreferences = null,
}) =>
  isPlatformAdmin(user)
  || isTeamLeadForOrganization(user, responsibleOrganizationId, userPreferences);

/**
 * Iz niza povezanih organizacija izvlači ID glavne odgovorne institucije.
 * @param {Array<object>} [relatedOrganizations] - `related_organizations` sa BE-a.
 * @returns {string} Normalizovan ID organizacije ili prazan string.
 */
export const resolveResponsibleOrganizationId = (relatedOrganizations = []) => {
  if (!Array.isArray(relatedOrganizations) || relatedOrganizations.length === 0) {
    return '';
  }
  const main = relatedOrganizations.find((organization) => organization?.main_responsible);
  const resolveOrganizationId = (organization) =>
    organization?.organization_id ??
    organization?.institution_id ??
    organization?.organizationId ??
    organization?.institutionId ??
    organization?.id ??
    null;
  return normalizeId(resolveOrganizationId(main || relatedOrganizations[0]));
};
